import createApiServices from './createApiServices'
import {OUTPUT_REPORT_TIMEOUT_MS, PRODUCTION_PERFORMANCE_TIMEOUT_MS, PRODUCTION_PERFORMANCE_URL} from '../../configs/productionPlanning'

/**
 * Cổng gọi service `production-planning-service` (tên cũ: `production-performance`).
 *
 * Đường dẫn đặt theo TÍNH NĂNG, không theo mã module nữa (API-CHANGES-FOR-FE.md, 09/09/2026):
 *
 *   /config/standard-time/*   — định mức thời gian, trước là M16 / `/kpi/*`
 *   /planning/capacity/*      — hoạch định năng lực, trước là M17 / `/kpi/workload/*`
 *   /control/output/*         — kiểm soát sản lượng, trước là M18 / `/kpi/output/*`
 *   /performance-records/* · /summary/* · /heartbeat/*   — không đổi
 *
 * Path `/kpi/*` cũ vẫn chạy song song nhưng KHÔNG dùng nữa: file này đã chuyển hết sang tên mới, BE
 * sẽ gỡ path cũ khi FE xác nhận. Request và response giữ nguyên 100%, đợt này chỉ đổi đường dẫn.
 *
 * KHÔNG có lớp dữ liệu mẫu: mọi hàm đều gọi thật. Endpoint nào service chưa có thì màn hình báo lỗi
 * thẳng — cố ý, để không ai nhầm số bịa là số thật. Hiện chỉ còn hai nút Xuất Excel của M17/M18 là
 * chưa có endpoint (xem cuối file).
 *
 * Service không có global prefix, và bọc kết quả trong envelope `{success, data, pagination?}` —
 * đúng dạng mà `request` / `requestPaginated` bên dưới đang bóc.
 */

const api = createApiServices({baseUrl: PRODUCTION_PERFORMANCE_URL, timeout: PRODUCTION_PERFORMANCE_TIMEOUT_MS})

/**
 * Đọc Blob thành chuỗi. `Blob.prototype.text()` không có trên Safari < 14 / Edge cũ, và CRA không
 * polyfill nó — thiếu thì mọi lỗi blob suy biến về thông báo chung, mất mã nghiệp vụ. `FileReader`
 * thì có từ IE10, nên lấy làm đường lui.
 */
const blobToText = (blob) => {
    if (typeof blob.text === 'function') return blob.text()

    return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(reader.error)
        reader.readAsText(blob)
    })
}

/**
 * Bóc thân lỗi của endpoint tải file.
 *
 * Với `responseType: 'blob'`, trình duyệt trả `xhr.response` là Blob **kể cả khi status 4xx**, nên
 * `error.response.data` là một Blob chưa parse. `helpers/apiError.js` đọc `bodyOf(error).code`, mà
 * Blob không có `.code` — nên `errorCode()` trả `null` và mọi nhánh xử lý theo mã nghiệp vụ thành
 * code chết (`M18_E003` — vượt giới hạn dòng khi xuất là ca rõ nhất).
 *
 * Interceptor ở `createApiServices.js` đã cố ý KHÔNG trải phẳng Blob để thân còn nguyên tới đây;
 * bước còn thiếu là đọc nó ra. `blob.text()` là async nên chỉ làm được ở tầng này, không làm được
 * trong interceptor đồng bộ.
 *
 * Thân không phải JSON (HTML của gateway, body rỗng) thì trả lại lỗi gốc — không nuốt.
 */
const unwrapBlobError = async (error) => {
    const data = error && error.response && error.response.data

    if (typeof Blob === 'undefined' || !(data instanceof Blob)) throw error

    let parsed = null

    try {
        parsed = JSON.parse(await blobToText(data))
    } catch (e) {
        // Thân không phải JSON (HTML của gateway, body rỗng) — trả lại lỗi gốc, không nuốt.
        throw error
    }

    // Ném `Error` có gắn thuộc tính, không ném object trần: giữ đúng hình dạng phẳng mà
    // `helpers/apiError.js` đọc (`error.code` / `error.status` / `error.message`) đồng thời có
    // stack trace thật. Object trần vừa mất stack vừa vi phạm `no-throw-literal`.
    throw Object.assign(new Error(parsed.message || error.message), parsed, {
        status: error.response.status,
    })
}

/**
 * Bóc envelope `{success, data}` của service.
 *
 * ⚠️ LỖI KHÔNG giữ nguyên `AxiosError`: interceptor của `createApiServices` trải phẳng mọi lỗi có
 *    thân JSON thành `{...data, status, message}` — **không có `.response`**. Màn hình phải đọc
 *    qua `views/ProductionPlanning/helpers/apiError.js` (`errorStatus` / `errorCode` /
 *    `errorMessage` / `errorDetails`), tuyệt đối không đọc `error.response.status`.
 *
 *    Các mã quyết định hiện thông báo nào: 403 → E-101 / TT-06, 409 → E-202 / TT-03,
 *    503 → "chưa đấu nối" (M17 §8.3 · M18 BR-D10).
 */
const request = async (args) => {
    try {
        const body = await api.makeAuthRequest(args)

        return body && Object.prototype.hasOwnProperty.call(body, 'data') ? body.data : body
    } catch (error) {
        return unwrapBlobError(error)
    }
}

/**
 * Bản có phân trang: trả cả `pagination` để thanh phân trang dựng đúng tổng số dòng.
 *
 * `meta` chuyển tiếp NGUYÊN VẸN nếu service có trả — dùng ở `getOutputRecords` (SCR-M18-002) để biết
 * `stage.countUnit`/`timeBasis` quyết định hiển thị cấu trúc cột item hay đơn hàng. `undefined` khi
 * service không trả field này (đa số endpoint phân trang khác), OutputDetailPage tự xử lý `null`.
 */
const requestPaginated = async (args) => {
    const body = await api.makeAuthRequest(args)

    return {data: body.data, pagination: body.pagination, meta: body.meta}
}

export const clean = (params) => Object.keys(params || {}).reduce((acc, key) => {
    const value = params[key]

    if (value === undefined || value === null || value === '') return acc

    /*
     * ⚠️ Mảng RỖNG cũng bị bỏ. `[].join(',')` ra chuỗi rỗng, tức URL mang `?actors=` — nhìn như
     *    "lọc theo tài khoản tên rỗng" chứ không như "không lọc", và nó đi vào cả log lẫn link
     *    người dùng gửi cho nhau. Service đọc ra `[]` nên kết quả vẫn đúng; đây là chuyện nói cho
     *    rõ, không phải sửa lỗi sai số.
     */
    if (Array.isArray(value)) {
        if (value.length === 0) return acc

        acc[key] = value.join(',')

        return acc
    }

    acc[key] = value

    return acc
}, {})

/* ================================================================================================
 * Luồng performance-records + summary — không đổi trong đợt đổi tên
 * ============================================================================================== */

/** Công đoạn của luồng `performance_records`. KHÁC danh mục 9 công đoạn decor của M16 — đừng lẫn. */
export const PERFORMANCE_STAGES = ['cutting', 'printing', 'pressing', 'sewing', 'embroidery', 'qc', 'packing']

export const PERFORMANCE_SHIFTS = ['morning', 'afternoon', 'night']

export const PERFORMANCE_SOURCES = ['manual', 'scanner', 'import']

/* ---- Heartbeat ---- */

/** GET /heartbeat/ping — không cần token, dùng để biết service local đã lên chưa. */
export const ping = () => request({url: '/heartbeat/ping', method: 'GET'})

/** GET /heartbeat/ready — 503 khi service chưa sẵn sàng (chưa nối được DB). */
export const ready = () => request({url: '/heartbeat/ready', method: 'GET'})

/* ---- Performance records ---- */

/**
 * GET /performance-records — lọc + phân trang + sort.
 * Lọc được: factoryCode, operatorCode, stage, shift, sku, orderNumber, source, from, to.
 * `sortBy` theo dạng `recordedAt.desc`. Bỏ trống `factoryCode` thì service lấy FACTORY_CODE của nó.
 */
export const getPerformanceRecords = (params = {}) => requestPaginated(
    {url: '/performance-records', method: 'GET', params: clean(params)}
)

/** GET /performance-records/{id} */
export const getPerformanceRecord = (id) => request({url: `/performance-records/${id}`, method: 'GET'})

/**
 * POST /performance-records.
 * Bắt buộc: operatorCode, stage, shift, sku, quantity, defectQuantity, durationSeconds, recordedAt.
 * `durationSeconds` tính bằng GIÂY và phải >= 1; `quantity` >= 1; `recordedAt` là ISO-8601.
 */
export const createPerformanceRecord = (payload) => request(
    {url: '/performance-records', method: 'POST', data: payload}
)

/** PATCH /performance-records/{id} — cập nhật một phần, không đụng `factoryCode`. */
export const updatePerformanceRecord = (id, payload) => request(
    {url: `/performance-records/${id}`, method: 'PATCH', data: payload}
)

/** DELETE /performance-records/{id} — 204, không có body trả về. */
export const deletePerformanceRecord = (id) => request({url: `/performance-records/${id}`, method: 'DELETE'})

/**
 * POST /performance-records/bulk — ingest theo lô, dùng BASIC AUTH chứ không phải token người dùng
 * (endpoint dành cho máy quét tại chuyền). Trả 207 khi có bản ghi bị loại, kèm `rejectedCount`.
 *
 * Phải truyền `basicAuth` (chuỗi base64 `user:pass` của CHÍNH service này) — cố tình không lấy
 * `REACT_APP_BASIC_AUTH_PLF` mặc định của `makeBasicAuthRequest` vì đó là credential của PLF,
 * service này không nhận.
 */
export const bulkCreatePerformanceRecords = (records, basicAuth) => api.makeRequest({
    url: '/performance-records/bulk',
    method: 'POST',
    data: {records},
    headers: {Authorization: `Basic ${basicAuth}`},
})

/* ---- Summary ----
 *
 * Cả bốn endpoint BẮT BUỘC có `from` và `to` (ISO-8601), và bị chặn nếu khoảng ngày vượt
 * `limits.maxQueryRangeDays` của service. `operatorCodes` truyền mảng — `clean` tự nối bằng dấu
 * phẩy, đúng dạng service đang tách.
 *
 * Ba endpoint gom nhóm trả cùng một khuôn:
 *   {dimension, range: {from, to}, rows: [metrics], total: metrics}
 * với metrics = {key, recordCount, totalQuantity, totalDefectQuantity, totalDurationSeconds,
 *                goodQuantity, defectRate, unitsPerHour, secondsPerUnit}
 * `key` là operatorCode / stage / 'YYYY-MM-DD' tuỳ endpoint — ngày gom theo UTC.
 */

/** GET /summary/by-operator — gom theo nhân viên. */
export const getSummaryByOperator = (params) => request(
    {url: '/summary/by-operator', method: 'GET', params: clean(params)}
)

/** GET /summary/by-stage — gom theo công đoạn. */
export const getSummaryByStage = (params) => request(
    {url: '/summary/by-stage', method: 'GET', params: clean(params)}
)

/** GET /summary/daily — gom theo ngày (UTC). */
export const getSummaryDaily = (params) => request(
    {url: '/summary/daily', method: 'GET', params: clean(params)}
)

/**
 * GET /summary/leaderboard — xếp hạng theo `unitsPerHour` giảm dần, trả `{range, rows}` với mỗi
 * dòng có thêm `rank`. `minQuantity` để loại nhân viên sản lượng quá thấp (vài sản phẩm lẻ tạo ra
 * năng suất ảo); `limit` tối đa 200, mặc định 20.
 */
export const getLeaderboard = (params) => request(
    {url: '/summary/leaderboard', method: 'GET', params: clean(params)}
)

/* ------------------------------------------------------------------------------------------------
 * M16 — Cấu hình chỉ số tiêu chuẩn
 * ---------------------------------------------------------------------------------------------- */

/** SCR-M16-001 bảng T1 — danh sách công đoạn kèm thống kê cấu hình. */
export const getStages = () => request({url: '/config/standard-time/stages', method: 'GET'})

/** SCR-M16-001 cảnh báo W1/W2/W3 và thẻ thống kê C1→C4 của SCR-M16-003. */
export const getCoverageSummary = () => request({url: '/config/standard-time/coverage/summary', method: 'GET'})

/**
 * SCR-M16-002 khi mở màn — công đoạn + toàn bộ nhóm ĐANG QUẢN LÝ.
 *
 * ⚠️ `groups` là tập CẤU HÌNH, KHÔNG phải "đang hiệu lực": nó cố ý gồm cả nhóm khai hiệu lực từ tháng
 *    sau lẫn nhóm đã hết hạn theo thời gian, để người dùng thấy đúng thứ mình vừa lưu. Muốn biết cái
 *    gì đang CHẠY thì lọc bằng `isEffectiveAt` (helpers/priority.js) hoặc đọc `uncoveredMaterials` mà
 *    service đã tính sẵn — đừng đếm thẳng trên `groups`.
 */
export const getStageDetail = (stageCode) => request({url: `/config/standard-time/stages/${stageCode}`, method: 'GET'})

/**
 * SCR-M16-002 nút B3 — ghi cả công đoạn trong một transaction (BR-M16-34).
 * `configUpdatedAt` là token chống ghi đè: lệch bản trong DB thì service trả 409 và UI hiện E-202,
 * không tự ghi đè (EC-15).
 */
export const saveStageGroups = (stageCode, payload) => request({url: `/config/standard-time/stages/${stageCode}/standard-groups`, method: 'PUT', data: payload})

/**
 * SCR-M16-002 nút "Lịch sử" — gồm cả nhóm ĐÃ HẾT HIỆU LỰC.
 *
 * Trả hai nguồn TÁCH RỜI, không phải một dòng thời gian: `groups` là từng BẢN của nhóm (chỉ số lúc
 * đó là bao nhiêu, áp cho phạm vi nào), `entries` là nhật ký từng LƯỢT LƯU (ai bấm, lúc nào, đổi bao
 * nhiêu nhóm, vì sao). Một lượt lưu chạm nhiều nhóm nên trộn lại là mất câu hỏi thứ nhất.
 */
export const getStageGroupsHistory = (stageCode) => request({url: `/config/standard-time/stages/${stageCode}/standard-groups/history`, method: 'GET'})

/** Danh mục NVL cho ô G.16 — tải một lần mỗi phiên rồi giữ trong bộ nhớ trình duyệt. */
export const getMaterials = () => request({url: '/config/standard-time/materials', method: 'GET'})

/** Danh mục SKU. `active` mặc định `true` — SKU tắt không vào thống kê nào (BR-M16-26). */
export const getSkus = (params = {}) => request({url: '/config/standard-time/skus', method: 'GET', params: clean({active: true, ...params})})

/**
 * Hộp thoại M1 "Dán danh sách SKU". Trả ba nhóm tách rời vì màn hình báo ba mức khác nhau:
 * thêm được, thêm được kèm cảnh báo (SKU đang tắt VẪN cho thêm — V-11), và bị loại (V-10).
 */
export const resolveSkus = (skuIds) => request({url: '/config/standard-time/skus/resolve', method: 'POST', data: {skuIds}})

/** SCR-M16-003 bảng T1 — SKU đang bật kèm tình trạng từng công đoạn. Một trang. */
export const getSkuCoverage = (params = {}) => requestPaginated({url: '/config/standard-time/coverage/skus', method: 'GET', params: clean(params)})

/** Trần `limit` mà service cho phép — xin cao hơn vẫn bị cắt về mức này. */
const COVERAGE_PAGE_SIZE = 200

/**
 * Trần số trang, CHỈ để một lỗi phân trang phía service không kéo thành vòng lặp vô tận.
 *
 * KHÔNG phải giới hạn nghiệp vụ: chạm trần là bất thường, và hàm nói ra bằng `truncated` chứ không
 * lặng lẽ trả một tập thiếu.
 */
const COVERAGE_MAX_PAGES = 50

/**
 * Toàn bộ SKU của bảng độ phủ, gộp hết các trang.
 *
 * Màn SCR-M16-003 lọc và đếm ở phía trình duyệt, nên phải có ĐỦ dòng mới đúng. Gọi một phát không
 * tham số chỉ được trang đầu (limit mặc định), và các SKU cần chú ý nhất — chưa gán quy trình —
 * lại hay nằm ở trang cuối, khiến bảng trống trơn trong khi thẻ tổng vẫn báo có.
 *
 * ⚠️ Trả `{rows, truncated, total}` chứ KHÔNG trả thẳng mảng.
 *
 *    Bản trước dừng ở trang 20 rồi `return rows` — im lặng. Vượt trần thì màn hình hiện một danh
 *    sách THIẾU mà không có dấu hiệu nào: không lỗi, không cảnh báo, thẻ tổng vẫn đếm đúng nên số
 *    trên thẻ và số dòng trong bảng lệch nhau mà không ai giải thích được. Đúng loại lỗi vừa gặp ở
 *    ô chọn SKU, chỉ ở ngưỡng cao hơn. Danh mục hiện còn xa trần, nhưng "còn xa" không phải lý do
 *    để hỏng im lặng khi chạm tới. (Cố ý KHÔNG ghi số SKU cụ thể ở đây: ba con số khác nhau đã
 *    từng nằm rải trong cùng một commit. Cần số thì đọc `pagination.total` của response.)
 *
 * `total` lấy từ `pagination` của service, hoặc `null` nếu service không trả — chỗ gọi phải xử lý
 * `null` chứ đừng coi là 0.
 */
export const getAllSkuCoverage = async (params = {}) => {
    const rows = []
    let page = 1
    let pages = 1
    let total = null
    /*
     * Cờ bật NGAY khi vòng lặp bị trần chặn, đối chiếu với số trang LỚN NHẤT từng thấy.
     *
     * Bẫy: sau vòng lặp, `pages` CHÍNH LÀ `pages` của response cuối. Trang cuối méo thì
     * `pagination.pages || 1` kéo nó về 1, vòng lặp trông như "hết trang" chứ không phải "chạm
     * trần", và mọi phép suy lại sau khi thoát đều cho `false` — im lặng trả tập thiếu, đúng kịch
     * bản mà comment trên `COVERAGE_MAX_PAGES` nêu làm lý do tồn tại của cái trần.
     * Vì vậy giữ `knownPages` = max mọi lượt, và chốt cờ ngay trong vòng lặp.
     */
    let hitCap = false
    let knownPages = 1

    while (page <= pages && page <= COVERAGE_MAX_PAGES) {
        const body = await requestPaginated({
            url: '/config/standard-time/coverage/skus',
            method: 'GET',
            params: clean({...params, page, limit: COVERAGE_PAGE_SIZE}),
        })
        const pagination = body.pagination || {}

        rows.push(...(body.data || []))
        pages = pagination.pages || 1
        knownPages = Math.max(knownPages, pages)
        if (total === null && typeof pagination.total === 'number') total = pagination.total
        page += 1

        if (page > COVERAGE_MAX_PAGES && page <= knownPages) hitCap = true
    }

    // `total` giữ nguyên `null` khi service không trả — đừng lấy `rows.length` làm mẫu số, câu cảnh
    // báo sẽ đọc thành "Chỉ tải được 50 trên 50 SKU", tử số bằng mẫu số đúng lúc phải báo động.
    /*
     * Chốt cờ bằng HAI bằng chứng độc lập, đừng chỉ tin `pages`.
     *
     * `hitCap` ngờ `pages` khai QUÁ LỚN, nhưng lại tin tuyệt đối `pages` khai QUÁ NHỎ. Service trả
     * `pages: 0` (hoặc thiếu hẳn `pagination`) kèm `total: 900` thì vòng lặp dừng sau trang đầu và
     * cờ báo "không bị cắt" — màn "SKU thiếu định mức" khi đó không hiện cảnh báo nào và lọc, đếm
     * trên 200/900 SKU. Đó đúng là kiểu hỏng im lặng mà cả hàm này sinh ra để diệt.
     *
     * `rows.length < total` là bằng chứng không phụ thuộc `pages`: đã biết tổng mà cầm về ít hơn
     * thì chắc chắn đang thiếu.
     */
    const thieuSoVoiTong = typeof total === 'number' && rows.length < total

    return {rows, truncated: hitCap || thieuSoVoiTong, total}
}

/**
 * SCR-M16-004 — tra cứu định mức theo SKU.
 * Service BẮT BUỘC dùng lại `lookupStandard` của domain (BR-M16-46): kết quả màn này và giờ tính
 * ở M17/M18 phải giống hệt, cài logic riêng cho màn hình là làm hai nơi lệch nhau.
 */
export const lookupBySku = (skuId, at) => request({url: '/config/standard-time/lookup', method: 'GET', params: clean({skuId, at})})

/* ------------------------------------------------------------------------------------------------
 * M16 — nhập / xuất Excel (SCR-M16-005)
 * ---------------------------------------------------------------------------------------------- */

/**
 * GET /config/standard-time/standard-groups/template — endpoint DUY NHẤT của màn này đã có thật.
 *
 * `stageCode` bỏ trống = mọi công đoạn cần định mức. `format`: `csv` (mặc định, mở bằng Excel) hoặc
 * `json` (thân request dựng sẵn cho PUT .../standard-groups, giữ nguyên mã NVL có số 0 đầu).
 *
 * ⚠️ File trả về là **CSV**, không phải .xlsx, và 7 cột đặt tên camelCase
 *    (`stageCode,skuScope,skuList,materialCode,rate,effectiveFrom,effectiveTo,priority,verifyStatus,note`) — khác bảng "Cấu trúc sheet
 *    DINH_MUC" đang mô tả trên giao diện. Xem ghi chú ở ImportExportPage.
 *
 * Tải bằng `request` + blob chứ KHÔNG trả URL cho thẻ <a>: service đã siết auth (T-B4 xong), mà
 * thẻ <a> không gửi được header Authorization nên sẽ nhận 401.
 */
export const downloadImportTemplate = (stageCode, format) => request({
    url: '/config/standard-time/standard-groups/template',
    method: 'GET',
    params: clean({stageCode, format}),
    responseType: 'blob',
})

/**
 * GET /config/standard-time/import-export — tải file định mức HIỆN TẠI.
 *
 * Cùng dữ liệu với `/config/standard-time/standard-groups/template`, service cố ý mở hai đường dẫn cho cùng một
 * `KpiM16Service.standardGroupTemplate`. Nhận về CSV (StreamableFile) nên phải đọc dạng blob rồi tự
 * tải xuống — không có `.xlsx`.
 */
export const exportStandardGroups = (stageCode) => request({
    url: '/config/standard-time/import-export',
    method: 'GET',
    params: clean({stageCode, format: 'csv'}),
    responseType: 'blob',
})

/**
 * POST /config/standard-time/standard-groups/import/preview — kiểm tra file, KHÔNG BAO GIỜ ghi.
 *
 * Nhận `.csv` và `.xlsx`, phân biệt theo phần mở rộng chứ không theo mimetype.
 *
 * Trả về:
 *   {
 *     file:     {name, format: 'csv'|'xlsx', rowCount, groupCount},
 *     stages:   [{stageCode, incomingCount, currentCount, expiringCount}],
 *     warnings: [{row, column, code, message}],
 *     blockers: [{stageCode, staleConfig, conflicts, rateViolations}],
 *     dryRun:   true,
 *     applied:  null
 *   }
 *
 * `blockers` là thứ CHẶN lượt ghi mà bản xem trước cố ý KHÔNG ném (route này không nhận `@Body()`
 * nên không truyền được `changeReason`; ném ở đây là người dùng mất luôn bản xem trước). Màn Nhập/Xuất
 * đọc `rateViolations` để hỏi lý do thay đổi TRƯỚC khi gọi `commitImport`.
 *
 * ⚠️ Xem trước theo CÔNG ĐOẠN, không theo dòng — vì lúc ghi service thay TOÀN BỘ nhóm của mỗi công
 *    đoạn có mặt trong file (`expiringCount` là số nhóm đang có sẽ hết hiệu lực). Công đoạn không
 *    xuất hiện trong file thì không bị đụng tới.
 *
 * ⚠️ File sai một dòng là hỏng cả lượt: service ném 400 kèm danh sách lỗi, KHÔNG có bản xem trước và
 *    KHÔNG ghi một phần. Đọc lỗi bằng `importIssues(e)` bên dưới.
 */
export const previewImport = (file) => {
    const formData = new FormData()

    formData.append('file', file)

    return request({
        url: '/config/standard-time/standard-groups/import/preview',
        method: 'POST',
        data: formData,
        headers: {'Content-Type': 'multipart/form-data'},
        timeout: 0,
    })
}

/**
 * POST /config/standard-time/standard-groups/import với `dryRun=false` — ghi thật.
 *
 * Không có token xem trước: muốn ghi thì gửi LẠI chính file đó. Trường multipart luôn là chuỗi, và
 * service chỉ coi `'false'`/`'0'` là ghi thật, mọi giá trị khác đều giữ ở chế độ xem trước.
 *
 * Trả về cùng khuôn với preview nhưng `dryRun: false` và `applied` là mảng kết quả ghi từng công đoạn.
 */
export const commitImport = (file, changeReason) => {
    const formData = new FormData()

    formData.append('file', file)
    formData.append('dryRun', 'false')

    if (changeReason) formData.append('changeReason', changeReason)

    return request({
        url: '/config/standard-time/standard-groups/import',
        method: 'POST',
        data: formData,
        headers: {'Content-Type': 'multipart/form-data'},
        timeout: 0,
    })
}

/**
 * Bóc danh sách lỗi theo dòng ra khỏi lỗi 400 của hai hàm trên.
 *
 * Interceptor ở `createApiServices` đã trải phẳng thân lỗi lên chính đối tượng reject, nên KHÔNG có
 * `e.response.data` — danh sách nằm thẳng ở `e.data`.
 */
export const importIssues = (error) => {
    const issues = (error || {}).data

    return Array.isArray(issues) ? issues : []
}

/* ------------------------------------------------------------------------------------------------
 * M17 — Khối lượng công việc & nhân lực
 * ---------------------------------------------------------------------------------------------- */

/**
 * SCR-M17-001. Tham số tính toán đi qua query để P-03 chạy được: người không có
 * `m17.params.manage` vẫn đổi thử được trong phiên xem, chỉ là không lưu.
 */
export const getWorkloadReport = (params) => request({url: '/planning/capacity', method: 'GET', params: clean(params)})

/** Khung trượt mức SKU (E-A). */
export const getWorkloadStageSkus = (stageCode, params) => request({url: `/planning/capacity/stages/${stageCode}/skus`, method: 'GET', params: clean(params)})

/** Khung trượt mức item (E-B) — tối đa 200 item đầu tiên. */
export const getWorkloadStageItems = (stageCode, skuId, params) => request({url: `/planning/capacity/stages/${stageCode}/skus/${skuId}/items`, method: 'GET', params: clean({limit: 200, ...params})})

export const getWorkloadParams = () => request({url: '/planning/capacity/params', method: 'GET'})

/** Cấu hình dùng chung CẢ NHÀ MÁY, không per-user (BR-M17-35 — A lưu thì B thấy). */
export const saveWorkloadParams = (payload) => request({url: '/planning/capacity/params', method: 'PUT', data: payload})

export const saveStageCapacity = (stageCode, payload) => request({url: `/planning/capacity/stages/${stageCode}/capacity`, method: 'PUT', data: payload})

/** Tỷ lệ mức khẩn đã lưu — dùng CHUNG cả nhà máy, chỉ áp cho nguồn `currentOutput` (FR-M17-18). */
export const getUrgencyRatios = () => request({url: '/planning/capacity/urgency-ratios', method: 'GET'})

/**
 * 100 bản cấu hình đã lưu gần nhất của MỘT loại (`params` | `urgencyRatios`).
 *
 * Trả kèm luôn `payload` chứ không chỉ tên: mỗi bản chỉ vài trăm byte (6 số hoặc 18 tỷ lệ), nên nạp
 * cả danh sách rẻ hơn hẳn việc gọi thêm một lượt mỗi khi người dùng chọn một bản để xem thử.
 */
export const getConfigHistory = (kind) => request({url: '/planning/capacity/config-history', method: 'GET', params: {kind}})

/** Payload cùng định dạng chuỗi `g1.4=50,g3.1_5=0` như tham số `ratios` của báo cáo. `ratios` rỗng/không gửi = xoá. */
export const saveUrgencyRatios = (ratios, name) => request({url: '/planning/capacity/urgency-ratios', method: 'PUT', data: {ratios, name}})

/** Nút "Xuất Excel" của SCR-M17-001 — cùng nguồn với `getWorkloadReport`, chỉ khác định dạng trả về. */
export const exportWorkloadReport = (params) => request({url: '/planning/capacity/export', method: 'GET', params: clean(params), responseType: 'blob'})

/* ------------------------------------------------------------------------------------------------
 * M18 — Sản lượng & giờ định mức theo nhân viên
 * ---------------------------------------------------------------------------------------------- */

/**
 * SCR-M18-001 — bảng sản lượng & giờ định mức, gộp theo TÀI KHOẢN.
 *
 * ⚠️ Endpoint NẶNG, và nặng theo đúng nghĩa không tránh được: nó đọc log thao tác ở mức từng lượt
 *    quét để tính các chỉ số về DANH TÍNH sản phẩm — bao nhiêu sản phẩm khác nhau, bao nhiêu lần
 *    làm lại, lượt nào là lần đầu của chính người đó. Một tháng là nửa triệu dòng log, và danh
 *    tính thì không cộng dồn được nên không tổng hợp trước được.
 *
 *    Vì vậy nó dùng trần chờ RIÊNG (`OUTPUT_REPORT_TIMEOUT_MS`, 90 s) thay cho 30 s chung của app.
 *    Lý do đầy đủ kèm bảng số đo nằm ở `configs/productionPlanning.js` — đọc trước khi chỉnh con
 *    số này, nhất là đoạn giải thích vì sao trần NGẮN lại làm tải TĂNG.
 *
 *    Khoảng lọc càng rộng càng lâu: hôm nay 0,6 s · 7 ngày 2,6 s · 30 ngày 8,6 s · 59 ngày tới
 *    21,7 s. Màn hình nên mặc định khoảng hẹp và đừng tự gọi lại khi người dùng chưa bấm.
 */
export const getOutputReport = (params) =>
    request({url: '/control/output/report', method: 'GET', params: clean(params), timeout: OUTPUT_REPORT_TIMEOUT_MS})

/**
 * SCR-M18-005 — CÙNG dữ liệu với `getOutputReport`, gộp theo công đoạn thay vì theo tài khoản.
 *
 * Trả `stages[]` (mỗi phần tử có `summary` + `actors[]`) thay cho `actors[]` (chứa `stages[]`). Phần
 * tử CON của hai đường là cùng một object (tài khoản × công đoạn, đủ cả `stageCode` lẫn `actor`), nên
 * màn hình dùng chung một bộ cột — xem `OutputReportPage` prop `groupBy`.
 *
 * Đường riêng chứ không phải `report?groupBy=stage`: một endpoint đổi hình dạng response theo tham số
 * query buộc mọi client phải phân nhánh theo giá trị tham số mới đọc nổi kết quả.
 */
export const getOutputReportByStage = (params) =>
    request({url: '/control/output/report/stages', method: 'GET', params: clean(params), timeout: OUTPUT_REPORT_TIMEOUT_MS})

/** SCR-M18-002 — chi tiết sản lượng, 50 dòng một trang. */
export const getOutputRecords = (params) => requestPaginated({url: '/control/output/records', method: 'GET', params: clean({limit: 50, ...params})})

/** SCR-M18-003 — danh sách này KHÔNG làm đổi số liệu ở báo cáo. */
export const getOutputSuspects = (params) => requestPaginated({url: '/control/output/suspects', method: 'GET', params: clean({limit: 50, ...params})})

/** Danh sách dòng log bị loại kèm lý do — ba lý do tách rời (BR-A03). */
export const getOutputDropped = (params) => requestPaginated({url: '/control/output/dropped', method: 'GET', params: clean({limit: 50, ...params})})

/**
 * Nguồn duy nhất cho bảng giải thích chỉ số và chú thích rê chuột (M18 §E.2). Lỗi thì màn hình
 * quay về bản dự phòng trong `constant/glossary.js` — hai nơi viết riêng thì sau vài lần sửa sẽ
 * lệch nhau, và người dùng sẽ tin vào cái sai.
 */
export const getOutputGlossary = () => request({url: '/control/output/glossary', method: 'GET'})

/**
 * Nút "Xuất Excel" của SCR-M18-001. `dataset` mặc định `report` ở service — không cần gửi khi xuất
 * đúng báo cáo đang xem.
 *
 * ⚠️ Dùng CHUNG `OUTPUT_REPORT_TIMEOUT_MS` với hai đường báo cáo, và đó không phải sự rộng tay:
 *    `/control/output/export` gọi thẳng `this.service[dataset](query)` — đúng `report()`/`stages()`
 *    vừa được nới lên 90 giây — rồi còn dựng thêm file xlsx. Để nó ở trần 30 giây mặc định thì nút
 *    Xuất Excel sẽ hết giờ ở ĐÚNG những khoảng mà màn báo cáo vừa được mở rộng để phục vụ, và người
 *    dùng nhận một lỗi timeout chung chung SAU KHI server đã làm xong việc. (Vòng review 03/11/2026.)
 */
export const exportOutputReport = (params) => request({
    url: '/control/output/export',
    method: 'GET',
    params: clean(params),
    responseType: 'blob',
    timeout: OUTPUT_REPORT_TIMEOUT_MS,
})

/* ------------------------------------------------------------------------------------------------
 * FAC-26417 — Giờ làm thực tế (mẫu số của mọi chỉ số hiệu suất)
 * ---------------------------------------------------------------------------------------------- */

/**
 * SCR Giờ làm thực tế — lưới tài khoản × ngày.
 *
 * `from`/`to` là NGÀY LỊCH `YYYY-MM-DD` theo giờ VN, KHÔNG phải mốc ISO đầy đủ như các endpoint báo
 * cáo khác của M18. Service cắt ngày bằng UTC+7 cố định (`vnDayOf`), cố ý khác `/summary/*` gom theo
 * UTC: ca sáng bắt đầu 08:00 giờ VN = 01:00 UTC, gom theo UTC là ghép cả ca sáng vào ngày không có
 * giờ làm. Gửi mốc ISO vào đây thì service cắt 10 ký tự đầu, tức lấy ngày UTC — lệch đúng một ngày
 * với mọi mốc từ 00:00 đến 07:00 giờ VN.
 *
 * Đọc được bằng cả `fac_admin` lẫn `fac_ffm`; chỉ `fac_admin` ghi được (xem `saveActualHours`).
 */
export const getActualHours = (params) => request({url: '/control/output/actual-hours', method: 'GET', params: clean(params)})

/**
 * Ghi hàng loạt một lượt. `hours: null` nghĩa là **XOÁ** ô đó, khác hẳn `hours: 0` nghĩa là "đã khai:
 * hôm đó không làm" — xem `helpers/actualHours.js`.
 *
 * `changeReason` chỉ BẮT BUỘC khi lượt ghi chạm ô đã có giá trị (BR-1.4), và service là nơi quyết
 * định điều đó chứ không phải FE: nó biết ô nào đã tồn tại trong DB. FE đoán trước chỉ để hỏi lý do
 * đúng lúc thay vì để người dùng ăn 400 sau khi đã gõ xong cả tuần.
 */
export const saveActualHours = (entries, changeReason) => request({
    url: '/control/output/actual-hours',
    method: 'PUT',
    data: {entries, ...(changeReason ? {changeReason} : {})},
})

/* ------------------------------------------------------------------------------------------------
 * FAC-26417 — nhập chấm công & bản đồ mã nhân viên ↔ tài khoản
 * ---------------------------------------------------------------------------------------------- */

/** Gói một file vào multipart kèm các trường phụ. Rỗng/undefined thì KHÔNG gửi trường đó. */
const fileForm = (file, fields = {}) => {
    const formData = new FormData()

    formData.append('file', file)

    Object.keys(fields).forEach((key) => {
        const value = fields[key]

        if (value === undefined || value === null || value === '') return

        // Mảng RỖNG cũng là "không gửi". Gửi `'[]'` thì service nhận một trường nó phải đi kiểm,
        // và mọi thông báo lỗi của trường đó rơi vào một lượt nhập không hề dùng tới nó.
        if (Array.isArray(value) && value.length === 0) return

        // Trường multipart luôn là CHUỖI. Mảng/object phải tự `JSON.stringify`, nếu không
        // `FormData` gọi `String(value)` và gửi lên `[object Object]` — service đọc ra chuỗi đó và
        // ném "must be an array". Phía service có `@Transform` đọc ngược lại chuỗi JSON này.
        formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value))
    })

    return formData
}

const upload = (url, formData) => request({
    url,
    method: 'POST',
    data: formData,
    // `timeout: 0` như hai hàm nhập của M16: file chấm công 2.000 dòng và service phải tra bản đồ
    // theo từng ngày, dễ vượt timeout mặc định.
    headers: {'Content-Type': 'multipart/form-data'},
    timeout: 0,
})

/**
 * Bản đồ mã nhân viên ↔ tài khoản.
 *
 * `at` = xem bản đồ TẠI ngày đó (`YYYY-MM-DD`), bỏ trống = hôm nay. `includeHistory` trả cả dòng đã
 * hết hiệu lực — đó chính là phần lịch sử, đừng lọc nó ra khi muốn xem bản đồ của một kỳ đã qua.
 */
export const getWorkerAccounts = (params) => request({url: '/control/worker-accounts', method: 'GET', params: clean(params)})

/**
 * Xem trước nhập danh sách tài khoản. KHÔNG ghi gì — bất biến của endpoint, không phải tuỳ chọn.
 *
 * ⚠️ Service chỉ đọc sheet **`3D`** của workbook. Sheet `Chat nội bộ` của cùng file nói khác về cùng
 *    những người ấy (đo 27/10/2026: tráo tài khoản của `00503` và `00507` cho nhau) — đừng "sửa"
 *    thành đọc cả hai.
 *
 * File sai KHÔNG ném: trả về `issues` theo dòng để màn hình chỉ đúng chỗ phải sửa trong Excel.
 */
export const previewWorkerAccountsImport = (file) => upload('/control/worker-accounts/import/preview', fileForm(file))

/**
 * File MẪU .xlsx cho danh sách tài khoản.
 *
 * ⚠️ Lý do phải có: file thật của bên 3D KHÔNG có hai cột `Hiệu lực từ` / `Hiệu lực đến`, nên không
 *    có file mẫu thì người dùng không có cách nào biết chúng tồn tại — mà đó là đường duy nhất để
 *    khai chuyển tổ (đóng tài khoản cũ, mở tài khoản mới) ngay trong file.
 *
 * Sheet `3D` của file mẫu CHỈ có tiêu đề: nhập thẳng file chưa sửa gì phải đọc ra 0 dòng. Ví dụ nằm
 * ở sheet `Hướng dẫn`, sheet mà service không đọc.
 *
 * Tải bằng `request` + blob chứ không trả URL cho thẻ <a>: thẻ <a> không gửi được header
 * Authorization nên sẽ nhận 401.
 */
export const downloadWorkerAccountsTemplate = () => request({
    url: '/control/worker-accounts/template',
    method: 'GET',
    responseType: 'blob',
})

/**
 * Nhập thật.
 *
 * `appliedFrom` áp cho dòng KHÔNG tự khai `Hiệu lực từ` trong file. Bỏ trống thì service lấy mốc
 * mặc định của nó và trả về `defaultedEffectiveFrom` — danh sách những dòng đã phải mượn mốc đó.
 *
 * `overrides` cho phép mỗi dòng đổi có một mốc riêng: một lượt nhập thường chỉ đổi vài dòng và mỗi
 * dòng vào một ngày khác nhau.
 *
 * ⚠️ `confirmClose` BẮT BUỘC khi có mã đổi tài khoản: tài khoản CŨ sẽ bị đóng hiệu lực, và việc đó
 *    không hoàn tác được bằng cách nhập lại file. Thiếu nó thì service trả 400 kèm danh sách mã.
 *    Dòng tự khai `Hiệu lực đến` thì KHÔNG cần — người dùng đã trả lời ngay trong file.
 */
export const importWorkerAccounts = (
    file,
    {appliedFrom, closeAt, overrides, closeMissing, confirmClose, changeReason} = {}
) =>
    upload(
        '/control/worker-accounts/import',
        /*
         * ⚠️ `closeAt` TÁCH khỏi `appliedFrom`: mốc áp dụng cho dòng mới và mốc đóng tài khoản vắng
         *    mặt là hai việc ngược chiều nhau. Gộp lại thì lùi mốc cho dòng mới cũng lùi mốc đóng,
         *    và khoảng hiệu lực bị đảo ngược. Bỏ trống `closeAt` = service đóng tại hôm nay.
         */
        fileForm(file, {appliedFrom, closeAt, overrides, closeMissing, confirmClose, changeReason})
    )

/**
 * Xem trước nhập **bảng chấm công tổng hợp** của HR — KHÔNG ghi gì.
 *
 * Trả `rows[]` với `dayHours`/`nightHours` tách riêng để màn hình hiện `3 + 5` chứ không chỉ `8`:
 * tổng một mình không nói được đó là ngày có ca đêm. `codes` là mã HR đã điền (`NL`, `P`) — phân
 * biệt "nghỉ phép" với "HR quên điền". `currentHours` khác `null` nghĩa là ô ĐANG CÓ giá trị và sẽ
 * bị ghi đè.
 */
export const previewTimesheetImport = (file) =>
    upload('/control/output/actual-hours/import/preview', fileForm(file))

/**
 * Nhập thật.
 *
 * `changeReason` chỉ là ghi chú THÊM — service tự sinh lý do kèm tên file, tài khoản và mốc thời
 * gian, nên guard BR-1.4 vẫn qua được dù người dùng không gõ gì.
 *
 * `overrides` là giờ người dùng SỬA TAY trên bản xem trước, dạng `{employeeCode, workDate, hours}`.
 * KHÔNG gửi `actor`: service tự quy mã nhân viên ra tài khoản theo NGÀY của dòng, y như dòng đọc
 * thẳng từ file — gửi tài khoản lên là mở đường ghi giờ vào tài khoản bất kỳ mà không qua bản đồ.
 * Khoá (mã, ngày) không có trong file bị service bỏ qua, không tạo dòng mới.
 */
export const importTimesheet = (file, changeReason, options = {}) =>
    upload('/control/output/actual-hours/import', fileForm(file, {
        changeReason,
        overrides: options.overrides,
    }))
